import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useSeo, serviceJsonLd } from "@/lib/seo";
import { PageHeader, ServiceCard, Loader } from "@/components/shared";
import ScopeOfPractice from "@/components/ScopeOfPractice";
import { formatINR } from "@/lib/format";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Check, Clock, Video, MapPin } from "lucide-react";

export function Services() {
  useSeo({
    title: "Services & Pricing — Family Constellation Sessions",
    description: "Family Constellation sessions in Ludhiana and online: individual 1:1 sessions, online sessions, and group workshops. See formats, duration and pricing.",
    path: "/services",
  });
  const [services, setServices] = useState(null);
  useEffect(() => { api.get("/services").then((r) => setServices(r.data)).catch(() => setServices([])); }, []);
  return (
    <div>
      <PageHeader crumb="Services" title="Ways to work together" intro="Whether one-to-one or in a group, in Ludhiana or online — here is how we can begin." />
      <section className="container-page pb-16 md:pb-24">
        <h2 className="text-2xl md:text-3xl text-earth mb-8" style={{ fontWeight: 600 }}>Family Constellation sessions in Ludhiana &amp; online</h2>
        {!services ? <Loader /> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((s) => <ServiceCard key={s.service_id} s={s} />)}
          </div>
        )}
        <div className="mt-14"><ScopeOfPractice /></div>
      </section>
    </div>
  );
}

export function ServiceDetail() {
  const { slug } = useParams();
  const [s, setS] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useSeo({
    title: s ? s.title : "Service",
    description: s ? s.shortDescription : undefined,
    path: `/services/${slug}`,
    jsonLd: s ? serviceJsonLd(s) : null,
  });

  useEffect(() => {
    api.get(`/services/${slug}`).then((r) => setS(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="container-page py-24 text-center text-earth/70">Service not found. <Link to="/services" className="text-gold-dark underline">All services</Link></div>;
  if (!s) return <Loader />;

  const Icon = s.format?.includes("Online") ? Video : MapPin;

  return (
    <div>
      <PageHeader crumb={s.title} title={s.title} />
      <section className="container-page pb-16 md:pb-24">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            <p className="text-xl text-earth/80 leading-relaxed mb-8">{s.fullDescription}</p>

            {s.whoIsItFor?.length > 0 && (
              <div className="mb-8">
                <h2 className="text-2xl text-earth mb-4" style={{ fontWeight: 600 }}>Who it's for</h2>
                <ul className="space-y-3">
                  {s.whoIsItFor.map((w, i) => <li key={i} className="flex gap-3 text-earth/80 text-lg"><Check className="text-gold shrink-0 mt-1" size={18} />{w}</li>)}
                </ul>
              </div>
            )}
            {s.whatsIncluded?.length > 0 && (
              <div className="mb-8">
                <h2 className="text-2xl text-earth mb-4" style={{ fontWeight: 600 }}>What's included</h2>
                <ul className="space-y-3">
                  {s.whatsIncluded.map((w, i) => <li key={i} className="flex gap-3 text-earth/80 text-lg"><Check className="text-gold shrink-0 mt-1" size={18} />{w}</li>)}
                </ul>
              </div>
            )}
            {s.preparationNotes && (
              <div className="mb-8 bg-surface-card border hairline rounded-lg p-6">
                <h3 className="text-lg text-earth mb-1" style={{ fontWeight: 600 }}>How to prepare</h3>
                <p className="text-earth/75">{s.preparationNotes}</p>
              </div>
            )}
            {s.faqs?.length > 0 && (
              <div>
                <h2 className="text-2xl text-earth mb-2" style={{ fontWeight: 600 }}>Questions</h2>
                <Accordion type="single" collapsible>
                  {s.faqs.map((f, i) => (
                    <AccordionItem key={i} value={`f-${i}`} className="border-b hairline">
                      <AccordionTrigger className="text-left text-earth hover:no-underline py-4">{f.question}</AccordionTrigger>
                      <AccordionContent className="text-earth/75 pb-4">{f.answer}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}
          </div>

          <aside className="lg:col-span-4">
            <div className="sticky top-28 bg-surface-card border hairline rounded-lg p-7">
              <div className="text-3xl text-earth font-display" style={{ fontWeight: 600 }}>{formatINR(s.price)}</div>
              <div className="text-sm text-earth/50 mb-5">{s.priceNote}</div>
              <div className="space-y-3 mb-6 text-earth/80">
                <div className="flex items-center gap-3"><Icon size={18} className="text-gold" /> {s.format}</div>
                <div className="flex items-center gap-3"><Clock size={18} className="text-gold" /> {s.durationLabel}</div>
              </div>
              {s.isBookable ? (
                <Link to={`/book?service=${s.slug}`} className="btn-gold w-full" data-testid="service-book-btn">Book this session</Link>
              ) : (
                <Link to={`/contact?interest=Workshop`} className="btn-gold w-full" data-testid="service-enquire-btn">Enquire</Link>
              )}
              {!s.isBookable && <p className="text-xs text-earth/50 mt-3 text-center">Group workshops are booked via the workshop calendar or on enquiry.</p>}
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
